import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';

import { environment } from 'src/environments/environment';
import { Tenant } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { MessageService } from 'src/services/message.service';

import { SharedTenantService } from '../shared-tenant.service';

interface ConfidenceDistribution {
  range: string;
  count: number;
  percentage: number;
}

interface MethodDistribution {
  method: string;
  count: number;
  avgConfidence: number;
  percentage: number;
}

interface LowConfidenceArticle {
  articleId: string;
  articleTitle: string;
  primaryCategory: string;
  primaryConfidence: number;
  chunkCount: number;
}

interface RecentClassification {
  date: string;
  classificationsCount: number;
  avgConfidence: number;
  newCategories: number;
}

interface DimensionalConfidenceAnalysis {
  dimensionId: string;
  dimensionName: string;
  strategy: string;
  totalClassifications: number;
  avgConfidence: number;
  confidenceDistribution: ConfidenceDistribution[];
  methodDistribution: MethodDistribution[];
  lowConfidenceArticles: LowConfidenceArticle[];
  recentClassifications: RecentClassification[];
}

@Component({
  selector: 'app-system-confidence-analysis',
  templateUrl: './system-confidence-analysis.component.html',
  styleUrls: ['./system-confidence-analysis.component.scss'],
})
export class SystemConfidenceAnalysisComponent implements OnInit {
  tenantId: string | null = null;
  dimensionId: string | null = null;
  tenant: Partial<Tenant> | null = null;
  analysisData: DimensionalConfidenceAnalysis | null = null;
  isLoading = true;
  error: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private messageService: MessageService,
    private sharedTenantService: SharedTenantService,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.tenantId = params.get('tenantId');
      this.dimensionId = params.get('dimensionId');

      // Get tenant from shared service
      this.tenant = this.sharedTenantService.getSelectedTenant();

      if (this.tenantId && this.dimensionId && this.tenant) {
        this.loadAnalysisData();
      } else {
        this.error = 'Missing tenant data, tenant ID, or dimension ID. Please select a tenant first.';
        this.isLoading = false;
      }
    });
  }

  loadAnalysisData() {
    if (!this.tenantId || !this.dimensionId) return;

    this.isLoading = true;
    this.error = null;
    this.analysisData = null;

    this.apiService
      .get<DimensionalConfidenceAnalysis>(
        `/knowledge-base/taxonomy/${this.tenantId}/dimensional-analysis/${this.dimensionId}`,
      )
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.analysisData = response;
        },
        error: (err) => {
          this.isLoading = false;
          this.error = 'Failed to load dimensional confidence analysis: ' + (err.message || 'Unknown error');
        },
      });
  }

  onRefresh() {
    this.loadAnalysisData();
  }

  goBack() {
    this.router.navigate(['/super-admin/taxonomy', this.tenantId, this.dimensionId]);
  }

  getConfidenceColor(confidence: number): string {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'danger';
  }

  getConfidenceText(confidence: number): string {
    if (confidence >= 0.9) return 'Excellent';
    if (confidence >= 0.8) return 'Very Good';
    if (confidence >= 0.7) return 'Good';
    if (confidence >= 0.6) return 'Fair';
    if (confidence >= 0.5) return 'Poor';
    if (confidence >= 0.4) return 'Very Poor';
    return 'Rejected';
  }

  getOverallSystemHealth(): string {
    if (!this.analysisData) return 'Unknown';

    const avgConfidence = this.analysisData.avgConfidence;
    const excellentPercentage = this.getDistributionItemByRange('0.9-1.0')?.percentage || 0;
    const lowConfidenceCount = this.analysisData.lowConfidenceArticles.length;

    if (avgConfidence >= 0.8 && excellentPercentage >= 40 && lowConfidenceCount <= 5) {
      return 'Excellent';
    } else if (avgConfidence >= 0.7 && excellentPercentage >= 25 && lowConfidenceCount <= 15) {
      return 'Good';
    } else if (avgConfidence >= 0.6 && lowConfidenceCount <= 30) {
      return 'Fair';
    } else {
      return 'Needs Attention';
    }
  }

  getOverallSystemHealthColor(): string {
    const health = this.getOverallSystemHealth();
    switch (health) {
      case 'Excellent':
        return 'success';
      case 'Good':
        return 'primary';
      case 'Fair':
        return 'warning';
      default:
        return 'danger';
    }
  }

  getMethodColor(method: string): string {
    switch (method.toLowerCase()) {
      case 'llm':
        return 'primary';
      case 'centroid':
        return 'secondary';
      default:
        return 'medium';
    }
  }

  getDistributionColor(range: string): string {
    if (range.includes('Excellent') || range.includes('Very Good')) return 'success';
    if (range.includes('Good') || range.includes('Fair')) return 'warning';
    return 'danger';
  }

  getDistributionItemByRange(range: string): ConfidenceDistribution | undefined {
    return this.analysisData?.confidenceDistribution.find((item) => item.range.startsWith(range));
  }

  getTrendIcon(current: number, previous: number): string {
    if (current > previous) return 'trending-up';
    if (current < previous) return 'trending-down';
    return 'remove';
  }

  getTrendColor(current: number, previous: number): string {
    if (current > previous) return 'success';
    if (current < previous) return 'danger';
    return 'medium';
  }

  trackByDistribution(index: number, item: ConfidenceDistribution): string {
    return item.range;
  }

  trackByMethod(index: number, item: MethodDistribution): string {
    return item.method;
  }

  trackByArticle(index: number, article: LowConfidenceArticle): string {
    return article.articleId;
  }

  trackByClassification(index: number, classification: RecentClassification): string {
    return classification.date;
  }

  viewArticleAnalysis(articleId: string) {
    if (!this.tenantId || !this.dimensionId) return;
    this.router.navigate(['/super-admin/taxonomy', this.tenantId, this.dimensionId, 'article', articleId]);
  }

  getArticleUrl(articleId: string): string {
    if (!this.tenant?.subdomain) {
      return `/dashboard/knowledge/articles/${articleId}/edit`;
    }

    // Construct tenant subdomain URL based on environment
    if (environment.production) {
      return `https://${this.tenant.subdomain}.${environment.rootDomain}/dashboard/knowledge/articles/${articleId}/edit`;
    } else {
      return `${environment.baseUrl}?subdomain=${this.tenant.subdomain}#!/dashboard/knowledge/articles/${articleId}/edit`;
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  formatDateAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    return this.formatDate(dateString);
  }

  async exportData() {
    if (!this.analysisData) return;

    try {
      const dataStr = JSON.stringify(this.analysisData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `taxonomy-confidence-analysis-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.messageService.show('Analysis data exported successfully');
    } catch (error) {
      this.messageService.showError('Failed to export data: ' + (error.message || 'Unknown error'));
    }
  }
}
